import {Yup} from 'utils/validators';
import {StringSchema} from 'yup';

import {
    BETA_COMP_PASS_LIMIT,
    calculateTotalNumberOfPasses,
} from 'pages/comp-pass/components/comp-pass-wizard/utils';

const phoneRegExp =
    /^(\+?1[-.\s]?)?\(?([2-9][0-9][0-9])\)?[-.\s]?([0-9][0-9]{2})[-.\s]?([0-9]{4})$/;

export const ERROR_MESSAGES_RECIPIENT = {
    email: {
        invalid: 'Invalid email.',
        required: 'This field is required.',
    },
    phoneNumber: {
        invalid: 'Invalid phone number.',
    },
    licensePlate: {
        invalid: 'Invalid license plate format.',
        notEnoughCharacters: 'Minimum of 1 character.',
        tooManyCharacters: 'Maximum of 8 characters.',
    },
    quantity: {
        notEnoughPasses: 'Minimum of 1 pass per event.',
        required: 'This field is required.',
        integersOnly: 'Must be a whole number.',
        maxQuantityExceeded: `Comp Pass limit exceeded. You can create up to ${BETA_COMP_PASS_LIMIT} passes at a time.`,
    },
};

/**
 * Yup schema for validating the comp pass recipient form.
 */
export const compPassRecipientValidationSchema = Yup.array()
    .of(
        Yup.object().shape(
            {
                email: Yup.string()
                    .label('Email')
                    .email(ERROR_MESSAGES_RECIPIENT.email.invalid)
                    .required(ERROR_MESSAGES_RECIPIENT.email.required),
                ccEmail: Yup.string()
                    .label('CC Email')
                    .when(
                        'ccEmail',
                        (
                            val: string,
                            schema: StringSchema<string | undefined>
                        ) => {
                            if (val?.length > 0) {
                                return schema.email(
                                    ERROR_MESSAGES_RECIPIENT.email.invalid
                                );
                            } else {
                                return Yup.string().notRequired();
                            }
                        }
                    ),
                phoneNumber: Yup.string()
                    .label('Phone Number')
                    .when(
                        'phoneNumber',
                        (
                            val: string,
                            schema: StringSchema<string | undefined>
                        ) => {
                            if (val?.length > 0) {
                                return schema.matches(
                                    phoneRegExp,
                                    ERROR_MESSAGES_RECIPIENT.phoneNumber.invalid
                                );
                            } else {
                                return Yup.string().notRequired();
                            }
                        }
                    ),
                licensePlate: Yup.string()
                    .label('License Plate')
                    .when(
                        'licensePlate',
                        (
                            val: string,
                            schema: StringSchema<string | undefined>
                        ) => {
                            if (val?.length > 0) {
                                return schema
                                    .min(
                                        1,
                                        ERROR_MESSAGES_RECIPIENT.licensePlate
                                            .notEnoughCharacters
                                    )
                                    .max(
                                        8,
                                        ERROR_MESSAGES_RECIPIENT.licensePlate
                                            .tooManyCharacters
                                    );
                            } else {
                                return Yup.string().notRequired();
                            }
                        }
                    ),
                quantity: Yup.number()
                    .label('Passes Per Event')
                    // eslint-disable-next-line no-undefined
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .required(ERROR_MESSAGES_RECIPIENT.quantity.required)
                    .min(1, ERROR_MESSAGES_RECIPIENT.quantity.notEnoughPasses)
                    .integer(ERROR_MESSAGES_RECIPIENT.quantity.integersOnly),
            },
            [
                ['email', 'email'],
                ['ccEmail', 'ccEmail'],
                ['phoneNumber', 'phoneNumber'],
                ['licensePlate', 'licensePlate'],
            ]
        )
    )
    .test({
        name: 'max-comp-passes',
        message: ERROR_MESSAGES_RECIPIENT.quantity.maxQuantityExceeded,
        params: {limit: BETA_COMP_PASS_LIMIT},
        test: (value, context) => {
            const numberOfEvents = context.options.context.numberOfEvents;
            const totalNumberOfPasses = calculateTotalNumberOfPasses({
                recipients: value,
                numberOfEvents,
            });

            const asNumber = isNaN(totalNumberOfPasses)
                ? 0
                : totalNumberOfPasses;

            return asNumber <= BETA_COMP_PASS_LIMIT;
        },
    });
